/**
 * Componente ProtectedRoute
 * 
 * Protege rutas que requieren autenticación.
 * Si el usuario no está autenticado, muestra la página de login.
 * Opcionalmente puede requerir roles específicos.
 */

import React, { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import LoginPage from './LoginPage';
import { isAuthDisabled } from '../config/authConfig';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  fallback?: ReactNode;
  loginPageProps?: {
    logoUrl?: string;
    appName?: string;
    companyName?: string;
  };
}

/**
 * Componente de acceso denegado
 */
const AccessDenied: React.FC<{ requiredRoles: string[]; userRoles: string[] }> = ({
  requiredRoles,
  userRoles,
}) => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg 
          className="w-8 h-8 text-red-600" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" 
          />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-800 mb-2">Acceso Denegado</h2>
      <p className="text-gray-600 mb-4">
        No tienes los permisos necesarios para acceder a esta sección.
      </p>
      <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
        <p className="mb-2">
          <strong>Roles requeridos:</strong> {requiredRoles.join(', ')}
        </p>
        <p>
          <strong>Tus roles:</strong> {userRoles.length > 0 ? userRoles.join(', ') : 'Ninguno'}
        </p>
      </div>
      <p className="mt-4 text-sm text-gray-500">
        Contacta al administrador si necesitas acceso.
      </p>
    </div>
  </div>
);

/**
 * ProtectedRoute - Componente que protege rutas
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  fallback,
  loginPageProps,
}) => {
  // Verificar si la autenticación está deshabilitada
  const authDisabled = isAuthDisabled();
  
  // IMPORTANTE: Siempre llamar useAuth (regla de hooks), pero solo usarlo si auth está habilitado
  // En modo deshabilitado, el AuthProvider retorna valores mock
  const { isAuthenticated, isLoading, user, hasRole } = useAuth();

  // Si la autenticación está deshabilitada, permitir acceso directo
  if (authDisabled) {
    return <>{children}</>;
  }

  // Mostrar loading mientras se verifica la autenticación
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  // Si no está autenticado, mostrar login
  if (!isAuthenticated) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <LoginPage {...loginPageProps} />;
  }

  // Verificar roles si se especificaron
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => hasRole(role));
    if (!hasRequiredRole) {
      return <AccessDenied requiredRoles={requiredRoles} userRoles={user?.roles || []} />;
    }
  }

  // Usuario autenticado y con permisos
  return <>{children}</>;
};

/**
 * HOC para proteger componentes (alternativa al componente)
 */
export function withAuth<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredRoles?: string[]
) {
  return function WithAuthComponent(props: P) {
    return (
      <ProtectedRoute requiredRoles={requiredRoles}>
        <WrappedComponent {...props} />
      </ProtectedRoute>
    );
  };
}

export default ProtectedRoute;
